/**
 * What a garment means for the 3D preview.
 *
 * Two rules, both trivial and both load-bearing, extracted from `main.ts`
 * because that module boots the editor on import and so cannot be tested. A
 * rule nobody can write a test for is a rule that quietly stops being true.
 *
 * The whole subtlety is that the two templates are **geometrically identical** —
 * the measured region map says so in its own source note, same eighteen
 * rectangles, same fill colours, only the printed labels differ. So a limb strip
 * is not "the arm regions": it is the arm regions on a shirt and the leg regions
 * on a pair of pants, and the only thing that can tell them apart is the
 * document's garment.
 */
import type { GarmentType, RegionId } from "../../shared/format";
import type { PartGroup } from "../viewer/types";

/**
 * Which part group a region belongs to, for this garment.
 *
 * Roblox composites a Shirt over Pants, and each covers a different set of
 * parts — so a torso stroke and a sleeve stroke invalidate different groups.
 * Re-uploading both on every stroke would waste half the bandwidth the
 * dirty-rect path exists to save.
 *
 * Reading the garment here is what stops a pants stroke repainting an arm and
 * leaving the leg it actually changed stale on screen.
 */
export function groupFor(region: RegionId, garment: GarmentType): PartGroup {
  if (region.startsWith("torso")) return "torso";
  return garment === "pants" ? "legs" : "arms";
}

/**
 * Every group a garment covers, for changes that are not localised.
 *
 * A garment switch is the clearest case: every pixel now means something
 * different, so there is no dirty rect to speak of.
 */
export function groupsOf(garment: GarmentType): PartGroup[] {
  return garment === "pants" ? ["torso", "legs"] : ["torso", "arms"];
}
